import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './motion.css';
import { ReactComponent as ClockIcon } from '@repo/common/assets/icons/16px/icon_16px_clock.svg';
import { ReactComponent as ReloadIcon } from '@repo/common/assets/icons/16px/icon_16px_reload.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_visible_outline.svg';
import { ReactComponent as CheckCircleIcon } from '@repo/common/assets/icons/16px/icon_16px_check-circle_outline.svg';
import { ReactComponent as TransferIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-transfer.svg';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';
import { ReactComponent as DownIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { JOB_LABEL, byJob, cn, isAssigneeOf } from './model/derive';
import { isMineOrToMe } from './model/taskViews';
import { useWorkboardCtx } from './model/useWorkboard';
import TaskCard, { YouLabel } from './TaskCard';
import PersonChip from './PersonChip';
import useFlip from './useFlip';
import useMedia, { MOBILE } from './useMedia';

const iconClass = 'h-4 w-4 shrink-0 text-icon-secondary';
const OPEN_MIN = 180;
const FOLDED_W = 48;
const GAP = 12;
const REOPEN_SLACK = 24;

export const COLUMN_ICON = {
  todo: <ClockIcon className={iconClass} />,
  in_progress: <ReloadIcon className={iconClass} />,
  in_review: <VisibleIcon className={iconClass} />,
  done: <CheckCircleIcon className={iconClass} />,
  sent_back: <TransferIcon className={iconClass} />,
};

const Empty = () => (
  <p className='rounded-[10px] border border-dashed border-grayscale-gray03 px-3 py-6 text-center text-[13px] text-text-placeholder'>
    Nothing here
  </p>
);

/**
 * 열 하나 — 펼침/접힘 사이를 폭이 부드럽게 바뀌며 오간다 (flex-grow · flex-basis 전환).
 * 접힌 열 = 폭 48px 세로 띠(chevron · 개수 · 세로 열 이름), 누르면 펼친다. 펼친 열 머리의 chevron 으로 다시 접는다.
 * 펼친 열은 최소 180px — 남는 폭은 나눠 갖고, 모자라면(Inbox 를 열거나 열을 다 펼치면) 스크롤 없이 일부 열을 자동으로 접는다.
 */
const Column = ({ col, tasks, folded, onFold, onUnfold, renderCard, emptySlot }) => {
  const label = col.label;
  return (
    <section
      aria-label={label}
      style={
        folded
          ? { flexGrow: 0, flexBasis: FOLDED_W, minWidth: FOLDED_W }
          : { flexGrow: 1, flexBasis: 0, minWidth: OPEN_MIN }
      }
      className={cn(
        'relative shrink-0 overflow-hidden rounded-[14px] bg-green-deep03/[0.05] transition-[flex-grow,flex-basis,min-width] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
        folded ? 'min-h-[200px] self-stretch' : '',
      )}
    >
      {/* 접힌 띠 */}
      <button
        type='button'
        aria-label={`Show ${label}`}
        aria-hidden={!folded}
        tabIndex={folded ? 0 : -1}
        onClick={onUnfold}
        className={cn(
          'absolute inset-0 flex flex-col items-center gap-2 py-3 transition-opacity duration-200 hover:bg-green-deep03/[0.04]',
          folded ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <ChevronIcon className={iconClass} />
        <span className='flex h-5 min-w-5 items-center justify-center rounded-full bg-grayscale-gray00 px-1.5 text-[12px] font-medium tabular-nums text-text-secondary'>
          {tasks.length}
        </span>
        <span className='text-[14px] font-medium text-text-primary [writing-mode:vertical-rl]'>
          {label}
        </span>
      </button>

      {/* 펼친 열 */}
      <div
        aria-hidden={folded}
        inert={folded || undefined}
        style={{ minWidth: OPEN_MIN }}
        className={cn(
          'flex flex-col gap-2 p-2 transition-opacity duration-200',
          // 접혀 있는 동안은 높이에 끼지 않게 띄워 둔다 (안의 카드 수만큼 띠가 길어지지 않게)
          folded
            ? 'pointer-events-none absolute left-0 top-0 w-[240px] opacity-0'
            : 'opacity-100 delay-100',
        )}
      >
        <header className='flex h-9 items-center gap-2 px-1.5'>
          {COLUMN_ICON[col.icon]}
          <span className='truncate text-[14px] font-medium leading-[140%] text-text-primary'>
            {label}
          </span>
          <span className='text-[13px] text-text-secondary'>({tasks.length})</span>
          {col.foldable && (
            <button
              type='button'
              aria-label={`Fold ${label}`}
              onClick={() => onFold(true)}
              className='ml-auto flex h-7 w-7 items-center justify-center rounded-md hover:bg-green-deep03/[0.08]'
            >
              <ChevronIcon className={cn(iconClass, 'rotate-180')} />
            </button>
          )}
        </header>
        {/* 상태가 바뀌어 다른 열로 가면 미끄러지듯 옮겨 가고, 새 카드는 살짝 떠오르며 나타난다 (useFlip) */}
        {tasks.map((t) => (
          <div key={t.id} data-flip-id={t.id}>
            {renderCard(t)}
          </div>
        ))}
        {tasks.length === 0 && (emptySlot ?? <Empty />)}
      </div>
    </section>
  );
};

/** 모바일 — 위 세그먼트(열 이름 + 개수)로 열을 하나씩 본다. 접기는 없다 */
const MobileColumns = ({ columns, renderCard, emptySlot }) => {
  const [active, setActive] = useState(columns[0]?.col.key);
  const current = columns.find((c) => c.col.key === active) ?? columns[0];
  return (
    <div className='flex flex-col gap-3'>
      <div
        role='tablist'
        aria-label='Columns'
        className='-mx-4 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
      >
        {columns.map(({ col, tasks }) => {
          const on = col.key === current?.col.key;
          return (
            <button
              key={col.key}
              type='button'
              role='tab'
              aria-selected={on}
              onClick={() => setActive(col.key)}
              className={cn(
                'flex h-11 shrink-0 items-center gap-1.5 rounded-full px-4 text-[14px] transition-colors duration-200',
                on
                  ? 'bg-green-deep03/[0.1] font-medium text-text-primary'
                  : 'text-text-secondary hover:bg-green-deep03/[0.05]',
              )}
            >
              {col.label}
              <span className='text-[13px] tabular-nums text-text-secondary'>{tasks.length}</span>
            </button>
          );
        })}
      </div>
      {current && (
        // 열을 바꾸면 카드가 차례로 떠오른다
        <div key={current.col.key} className='flex flex-col gap-2'>
          {current.tasks.map((t, i) => (
            <div
              key={t.id}
              className='td-rise'
              style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
            >
              {renderCard(t)}
            </div>
          ))}
          {current.tasks.length === 0 && (emptySlot?.(current.col) ?? <Empty />)}
        </div>
      )}
    </div>
  );
};

/**
 * 열 보드 — My tasks(Assigned to me · To review) · All tasks(By progress).
 * @param columns  [{ col, tasks }] — 열 정의와 그 열의 태스크 (정렬된 채로)
 * @param folded   (col) => 접혔는지 · onFold(col, folded)
 * @param emptySlot (col) => 빈 열 자리 (To review 빈 상태 등)
 */
export const ColumnBoard = ({ columns, folded, onFold, renderCard, emptySlot }) => {
  const mobile = useMedia(MOBILE);
  const flipRef = useFlip();
  const [width, setWidth] = useState(null);
  const [recent, setRecent] = useState([]);
  useLayoutEffect(() => {
    const el = flipRef.current;
    if (!el) return undefined;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [mobile, flipRef]);

  const n = columns.length;
  const fitIn = (w) =>
    Math.max(1, Math.floor((w - n * FOLDED_W - (n - 1) * GAP) / (OPEN_MIN - FOLDED_W)));
  const lastFit = useRef(null);
  let fit = width == null ? n : fitIn(width);
  if (width != null && lastFit.current != null && fit > lastFit.current) {
    fit = Math.max(lastFit.current, fitIn(width - REOPEN_SLACK));
  }
  useEffect(() => {
    lastFit.current = fit;
  });

  if (mobile) {
    return <MobileColumns columns={columns} renderCard={renderCard} emptySlot={emptySlot} />;
  }
  const order = (key) => {
    const i = recent.indexOf(key);
    return i === -1 ? recent.length + columns.findIndex((c) => c.col.key === key) : i;
  };
  const open = new Set(
    columns
      .filter(({ col }) => !(col.foldable && folded(col)))
      .map(({ col }) => col.key)
      .sort((a, b) => order(a) - order(b))
      .slice(0, fit),
  );
  const unfold = (col) => {
    setRecent((cur) => [col.key, ...cur.filter((k) => k !== col.key)]);
    if (col.foldable && folded(col)) onFold(col, false);
  };
  return (
    <div ref={flipRef} className='flex min-w-0 items-start gap-3'>
      {columns.map(({ col, tasks }) => (
        <Column
          key={col.key}
          col={col}
          tasks={tasks}
          folded={!open.has(col.key)}
          onFold={(f) => onFold(col, f)}
          onUnfold={() => unfold(col)}
          renderCard={renderCard}
          emptySlot={emptySlot?.(col)}
        />
      ))}
    </div>
  );
};

/* ── By assignee — 스윔레인 ─────────────────────────────── */

/** 행 순서 — 나를 맨 위에, 나머지는 직무 순. 태스크가 있는 사람만 */
const laneOwners = (tasks, ctx) => {
  const me = ctx.data.viewerId;
  return [...ctx.memberById.values()]
    .filter((m) => tasks.some((t) => isAssigneeOf(t, m.id)))
    .sort((a, b) => Number(b.id === me) - Number(a.id === me) || byJob(a, b));
};

const LaneHead = ({ m, count, open, onToggle, me }) => (
  <button
    type='button'
    aria-expanded={open}
    onClick={onToggle}
    className='flex w-full items-start gap-2 rounded-lg p-2 text-left hover:bg-green-deep03/[0.04]'
  >
    <DownIcon
      className={cn(iconClass, 'mt-2 transition-transform duration-300', !open && '-rotate-90')}
    />
    <span className='flex min-w-0 flex-col gap-0.5'>
      <span className='flex min-w-0 items-center gap-1.5'>
        <PersonChip members={[m]} size='L' />
        {me && <YouLabel />}
      </span>
      <span className='pl-10 text-[12px] text-text-secondary'>
        {JOB_LABEL[m.job] ?? m.job} · {count} task{count === 1 ? '' : 's'}
      </span>
    </span>
  </button>
);

/**
 * 스윔레인 — 행 = 팀원(태스크 있는 사람), 열 = By progress 와 같은 4단계.
 * 행 머리를 누르면 접는다(접힌 행은 칸에 개수만). 칸의 카드는 사람 칩 없이(행이 곧 담당자).
 * 모바일은 팀원 아코디언(내 행만 펼침) → 상태별 리스트.
 */
export const SwimlaneView = ({ columns, tasks, renderCard }) => {
  const ctx = useWorkboardCtx();
  const mobile = useMedia(MOBILE);
  const flipRef = useFlip();
  const me = ctx.data.viewerId;
  const owners = laneOwners(tasks, ctx);
  // 접힌 행 — 모바일은 내 행만 펼친 채로 시작
  const [closed, setClosed] = useState(() =>
    mobile ? new Set(owners.filter((m) => m.id !== me).map((m) => m.id)) : new Set(),
  );
  const toggle = (id) =>
    setClosed((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const cell = (m, col) => tasks.filter((t) => isAssigneeOf(t, m.id) && col.match(t, me));

  if (mobile) {
    return (
      <div className='flex flex-col gap-2'>
        {owners.map((m) => {
          const open = !closed.has(m.id);
          const all = tasks.filter((t) => isAssigneeOf(t, m.id));
          return (
            <section
              key={m.id}
              className='rounded-xl border border-green-deep03/10 bg-grayscale-gray00'
            >
              <LaneHead
                m={m}
                count={all.length}
                open={open}
                onToggle={() => toggle(m.id)}
                me={m.id === me}
              />
              <div
                className='td-collapse'
                data-open={open}
                aria-hidden={!open}
                inert={!open || undefined}
              >
                <div>
                  <div className='flex flex-col gap-3 px-3 pb-3'>
                    {columns.map((col) => {
                      const list = cell(m, col);
                      if (!list.length) return null;
                      return (
                        <div key={col.key} className='flex flex-col gap-2'>
                          <span className='text-[13px] font-medium text-text-secondary'>
                            {col.label} · {list.length}
                          </span>
                          {list.map((t) => (
                            <div key={t.id}>{renderCard(t, { lane: true })}</div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  const grid =
    'grid grid-cols-[200px_repeat(4,240px)] tabletV:grid-cols-[200px_repeat(4,minmax(0,1fr))]';
  return (
    <div className='-mx-8 overflow-x-auto px-8 pb-1 tabletV:mx-0 tabletV:px-0'>
      <div
        ref={flipRef}
        className='min-w-max overflow-hidden rounded-[14px] border border-green-deep03/10 bg-grayscale-gray00 tabletV:min-w-0'
      >
        <div
          className={cn(
            grid,
            'border-b border-green-deep03/[0.08] bg-green-deep03/[0.03] text-[13px] font-medium text-text-secondary',
          )}
        >
          <span className='px-4 py-2.5'>Teammate</span>
          {columns.map((col) => (
            <span
              key={col.key}
              className='flex items-center gap-1.5 border-l border-green-deep03/[0.06] px-3 py-2.5'
            >
              {COLUMN_ICON[col.icon]}
              {col.label}
              <span className='font-normal'>({tasks.filter((t) => col.match(t, me)).length})</span>
            </span>
          ))}
        </div>
        {owners.map((m) => {
          const open = !closed.has(m.id);
          return (
            <div
              key={m.id}
              className={cn(grid, 'border-b border-green-deep03/[0.06] last:border-b-0')}
            >
              <div className='p-1.5'>
                <LaneHead
                  m={m}
                  count={tasks.filter((t) => isAssigneeOf(t, m.id)).length}
                  open={open}
                  onToggle={() => toggle(m.id)}
                  me={m.id === me}
                />
              </div>
              {columns.map((col) => {
                const list = cell(m, col);
                return (
                  <div
                    key={col.key}
                    className='relative min-w-0 border-l border-green-deep03/[0.06]'
                  >
                    {/* 접힌 행 — 칸에 개수만 */}
                    <span
                      aria-hidden={open}
                      className={cn(
                        'absolute inset-x-0 top-3 text-center text-[13px] tabular-nums text-text-secondary transition-opacity duration-200',
                        open || !list.length ? 'opacity-0' : 'opacity-100',
                      )}
                    >
                      {list.length}
                    </span>
                    <div
                      className='td-collapse'
                      data-open={open}
                      aria-hidden={!open}
                      inert={!open || undefined}
                    >
                      <div>
                        {/* 여백을 접기 영역(overflow:hidden) 안에 둬야 카드 그림자가 네 방향 모두 보인다 */}
                        <div className='flex flex-col gap-2 p-2 pb-2.5'>
                          {list.map((t) => (
                            <div key={t.id} data-flip-id={`${m.id}:${t.id}`}>
                              {renderCard(t, { lane: true })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {!open && <div className='h-12' />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** 나와 관련된 카드인지 (All tasks) */
export const youFor = (t, ctx) => isMineOrToMe(t, ctx.data.viewerId);
